import "../styles/almanac.css";
import "../styles/wyrm.css";
import type { WyrmAlmanacRow } from "@highmarch/app";
import { Achievements, LevelCatalog } from "@highmarch/app";
import { bindSeg, fmtCompact, segHtml } from "../kit";
import { icon } from "../iconsKit";
import { portraitImg } from "../portraits";
import { medalIcon, paintedIconHtml } from "../featureIcons";
import { Almanac, AlmanacEnemy, AlmanacRare, AlmanacTower, almanacModel, trimOf } from "./almanacModel";
import { MEDAL_PX, affixesHtml, enemyDetailHtml, towerDetailHtml } from "./almanacDetail";
import { sheetHeadHtml } from "../components";

/** What the Almanac needs: the progress and the lifetime record. */
export interface AlmanacCtx {
  readonly catalog: LevelCatalog;
  readonly achievements: Achievements;
}

export type AlmanacTab = "raiders" | "bosses" | "rares" | "towers" | "elites";
const TABS: readonly { key: AlmanacTab; label: string }[] = [
  { key: "raiders", label: "Raiders" }, { key: "bosses", label: "Bosses" }, { key: "rares", label: "Rares" }, { key: "towers", label: "Towers" }, { key: "elites", label: "Elites" },
];
/** Portrait height on a grid card. */
const CARD_ART_PX = 62;

/** An almanac glyph: an open book (the kit has none). */
export function bookGlyph(size = 20, color = "currentColor"): string {
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 6c-2-1.5-5-2-8-1.5V19c3-.5 6 0 8 1.5 2-1.5 5-2 8-1.5V4.5C17 4 14 4.5 12 6z"/><path d="M12 6v14.5"/></svg>`;
}

/** A kill count on a card's art (UI pass 10-21: no caption line under every card). */
const killsBadge = (n: number): string => (n > 0 ? `<i class="kc">${icon("sword", 11, "#fff8e6")}${fmtCompact(n)}</i>` : "");
/** Where something not yet met comes: a lock and its level (or chapter) on the art. */
const lockBadge = (what: string): string => `<i class="lk">${icon("lock", 10, "#fff8e6")}${what}</i>`;

function enemyCard(e: AlmanacEnemy): string {
  const art = portraitImg(e.id, CARD_ART_PX, icon("skull", 34, "#fff8e6"));
  return e.seen
    ? `<button class="acard${e.boss ? " boss" : ""}${e.kills > 0 ? "" : " fresh"}" data-e="${e.id}"><div class="art">${art}${killsBadge(e.kills)}</div><b>${e.name}</b></button>`
    : `<button class="acard unseen${e.boss ? " boss" : ""}" data-e="${e.id}"><div class="art">${art}<i class="q disp">?</i>${lockBadge(`Lv ${e.firstLevel}`)}</div><b>???</b></button>`;
}

/** M36: a rare's card — its portrait in its sparkle, or a silhouette and its chapter until it has been seen. */
function rareCard(r: AlmanacRare): string {
  const art = portraitImg(r.id, CARD_ART_PX, icon("star", 34, "#fff8e6"));
  const glow = `style="--spark:#${r.spark.toString(16).padStart(6, "0")}"`;
  return r.seen
    ? `<div class="acard rare${r.kills > 0 ? "" : " fresh"}" ${glow}><div class="art">${art}<i class="rstar">★</i>${killsBadge(r.kills)}</div><b>${r.name}</b></div>`
    : `<div class="acard rare unseen" ${glow}><div class="art">${art}<i class="q disp">?</i>${lockBadge(`Ch ${r.chapter}`)}</div><b>???</b></div>`;
}

/** M40: the Starfall Wyrm's row atop the Rares page — a silhouette until it has been seen, then its record and set. */
function wyrmRow(w: WyrmAlmanacRow | null): string {
  if (!w) return "";
  const art = portraitImg("wyrm", 70, icon("star", 34, "#fff8e6"));
  return w.seen
    ? `<div class="wyrm-row"><div class="wy-art">${art}</div><div><b class="disp">${w.name}</b><small>${w.lore}</small><small>${w.kills > 0 ? `${icon("sword", 11)} ${fmtCompact(w.kills)} · ` : "Seen · not yet slain · "}${w.skinsOwned} of ${w.skinsTotal} Starfall treasures</small></div></div>`
    : `<div class="wyrm-row unseen"><div class="wy-art">${art}</div><div><b class="disp">??? · Legendary</b><small>Some nights the stars themselves fall. Watch the sky.</small></div></div>`;
}

function towerCard(t: AlmanacTower): string {
  const art = portraitImg(t.id, CARD_ART_PX, icon("tower", 34, "#fff8e6"));
  if (!t.owned) return `<button class="acard tower unseen" data-t="${t.id}"><div class="art">${art}${lockBadge(`Lv ${t.unlockLevel}`)}</div><b>${t.name}</b></button>`;
  const trim = trimOf(t.tier);
  const medal = t.tier >= 0 ? `<i class="tm">${paintedIconHtml(medalIcon(trim), MEDAL_PX + 4)}</i>` : "";
  return `<button class="acard tower trim-${trim}" data-t="${t.id}"><div class="art">${art}<i class="lv disp">${t.level}</i>${medal}${killsBadge(t.kills)}</div><b>${t.name}</b></button>`;
}

function gridHtml(a: Almanac, tab: AlmanacTab): string {
  if (tab === "raiders") return `<div class="agrid">${a.enemies.map(enemyCard).join("")}</div>`;
  if (tab === "rares") {
    return `${wyrmRow(a.wyrm)}<div class="agrid">${a.rares.map(rareCard).join("")}</div>`;
  }
  if (tab === "bosses") return `<div class="agrid">${a.bosses.map(enemyCard).join("")}</div>`;
  if (tab === "towers") {
    const tiers = a.mastery.map((m, i) => `<span class="tier trim-${trimOf(i)}">${paintedIconHtml(medalIcon(trimOf(i)), MEDAL_PX)}${m.name} ${fmtCompact(m.kills)}</span>`).join("");
    return `<div class="atiers">${tiers}</div><div class="agrid">${a.towers.map(towerCard).join("")}</div>`;
  }
  return affixesHtml(a);
}

/**
 * More → Almanac (FINISH_PLAN WP7, M20): raiders, bosses, towers (with their mastery trims) and the elite affixes and
 * Mythic powers, each tappable for its detail — "weak to" from the shared table (app counters.ts), the lifetime record
 * from the stats. Something not yet met shows as a silhouette with the level it comes at.
 */
export function showAlmanac(parent: HTMLElement, ctx: AlmanacCtx, start: AlmanacTab = "raiders"): HTMLElement {
  const sheet = document.createElement("div");
  sheet.className = "sheet panel almanac";
  parent.append(sheet);
  let tab: AlmanacTab = start;
  let detail: { kind: "e" | "t"; id: string } | null = null;
  const top = (): void => { const b = sheet.querySelector<HTMLElement>(".abody"); if (b) b.scrollTop = 0; };
  const render = (): void => {
    const a = almanacModel(ctx.catalog, ctx.achievements);
    const met = [...a.enemies, ...a.bosses].filter((e) => e.seen).length, all = a.enemies.length + a.bosses.length;
    const e = detail?.kind === "e" ? [...a.enemies, ...a.bosses].find((x) => x.id === detail!.id) : undefined;
    const t = detail?.kind === "t" ? a.towers.find((x) => x.id === detail!.id) : undefined;
    const body = e ? enemyDetailHtml(ctx.catalog, e) : t ? towerDetailHtml(ctx.catalog, a, t) : gridHtml(a, tab);
    sheet.innerHTML = `
      ${sheetHeadHtml({ title: "Almanac", sub: `${met} of ${all} foes met · ${a.towers.filter((x) => x.owned).length} of ${a.towers.length} towers`, portrait: paintedIconHtml("almanac", 52) })}
      ${detail ? `<button class="aback">${icon("back", 16)} All ${TABS.find((x) => x.key === tab)!.label.toLowerCase()}</button>` : segHtml(TABS, tab, "Almanac pages")}
      <div class="abody">${body}</div>`;
    sheet.querySelector<HTMLButtonElement>(".close")!.onclick = () => sheet.remove();
    bindSeg(sheet, (k) => { tab = k as AlmanacTab; render(); top(); });
    sheet.querySelector<HTMLButtonElement>(".aback")?.addEventListener("click", () => { detail = null; render(); });
    for (const c of sheet.querySelectorAll<HTMLButtonElement>(".acard[data-e]")) c.onclick = () => { detail = { kind: "e", id: c.dataset.e! }; render(); top(); };
    for (const c of sheet.querySelectorAll<HTMLButtonElement>(".acard[data-t]")) c.onclick = () => { detail = { kind: "t", id: c.dataset.t! }; render(); top(); };
    for (const c of sheet.querySelectorAll<HTMLElement>("[data-goto-e]")) c.onclick = () => { const id = c.dataset.gotoE!; tab = id.startsWith("boss_") ? "bosses" : "raiders"; detail = { kind: "e", id }; render(); top(); };
    for (const c of sheet.querySelectorAll<HTMLElement>("[data-goto-t]")) c.onclick = () => { tab = "towers"; detail = { kind: "t", id: c.dataset.gotoT! }; render(); top(); };
  };
  render();
  return sheet;
}
